import { useEffect, useRef, useState } from "react";
import { formatINR } from "@/utils/goldSilverParser";
import { useGoldSilver } from "@/context/GoldSilverContext";

const colorSelector = (isGold) => {
  return {
    panelBg: isGold ? "bg-[#FBF3DF]" : "bg-[#F4F5F6]",
    gradient: isGold
      ? "bg-[linear-gradient(90deg,#F0A500_0%,#FFDF10_50%,#F4B000_100%)]"
      : "bg-[linear-gradient(90deg,#C8C8C8_0%,#F4F4F4_48%,#BDBDBD_100%)]",
  };
};

function StatePriceCard({ state, price, isGold, href }) {
  const colors = colorSelector(isGold);
  return (
    <a href={href} className="shrink-0 w-[64%] md:w-[21%] rounded-xl overflow-hidden border border-gray-200 flex flex-col bg-white">
      <div className="px-3 py-2 flex flex-col gap-1">
        <span className="text-sm font-semibold whitespace-nowrap">{state}</span>
        <span className="text-xs text-gray-600 whitespace-nowrap">{isGold ? "24K Gold /g" : "Silver /g"}</span>
        <span className="text-base font-bold whitespace-nowrap">{formatINR(price)}</span>
      </div>
      <div className={`h-1.5 ${colors.gradient}`}></div>
    </a>
  );
}

const DOT_COUNT = 10;

export default function MultiCityPriceWidget({ defaultGold = true }) {
  const [isGold, setIsGold] = useState(defaultGold);
  const [activeDot, setActiveDot] = useState(0);
  const { multiStateOverview, multiStateLoading, loadMultiStateOverview } = useGoldSilver();
  const colors = colorSelector(isGold);
  const scrollRef = useRef(null);

  const overviewData = multiStateOverview;

  useEffect(() => {
    if (!overviewData && !multiStateLoading) {
      loadMultiStateOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewData]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const progress = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
    setActiveDot(Math.round(progress * (DOT_COUNT - 1)));
  };

  const scrollToDot = (index) => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    el.scrollTo({ left: (maxScroll * index) / (DOT_COUNT - 1), behavior: "smooth" });
  };

  if (!overviewData?.length) return null;

  return (
    <div className="flex flex-col gap-3 my-2">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h3 className="text-sm font-bold text-gray-800">सोने/ चांदी के भाव (Gold/ Silver Price Today)</h3>
        <div className={`${colors.panelBg} border border-gray-200 p-1 rounded-full flex items-center gap-1 font-semibold shrink-0 w-full md:w-[320px]`}>
          <button
            type="button"
            onClick={() => setIsGold(true)}
            className={`${isGold ? colors.gradient : ""} rounded-full flex-1 md:px-4 py-1 text-center text-sm`}
          >
            Gold
          </button>
          <button
            type="button"
            onClick={() => setIsGold(false)}
            className={`${!isGold ? colors.gradient : ""} rounded-full flex-1 md:px-4 py-1 text-center text-sm`}
          >
            Silver
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {overviewData.map((s) => {
          const href = `${process.env.NEXT_PUBLIC_WEBSITE_URL}/${isGold ? "gold-rates" : "silver-rates"}/${s.state_url}-${isGold ? "gold" : "silver"}-price-today`;
          return (
            <StatePriceCard
              key={s.state}
              state={s.state_hn || s.state}
              price={isGold ? s.gold : s.silver}
              isGold={isGold}
              href={href}
            />
          );
        })}
      </div>
      <div className="flex items-center justify-center gap-1.5">
        {Array.from({ length: DOT_COUNT }).map((_, index) => (
          <button
            key={index}
            type="button"
            aria-label={`Scroll to position ${index + 1}`}
            onClick={() => scrollToDot(index)}
            className={`h-1.5 rounded-full transition-all ${activeDot === index ? "w-4 bg-red-500" : "w-1.5 bg-gray-300"}`}
          />
        ))}
      </div>
    </div>
  );
}
